import { PermissionType } from 'src/store/slices/uiFlowPermissionsDataSlice';
import useAccessToken from './useAccessToken';
import router from 'next/router';
import { Products, useGetProductLevel } from '../utils/getProductLevel';
import useImpersonateSessionData from './useImpersonateSessionData';

const usePermission = () => {
  const config = sessionStorage.getItem('account-configurations');
  const prod = useGetProductLevel();
  const { isImpersonating, permissions: impersonatePermissions } = useImpersonateSessionData();

  const { menu: { menuItems = [] } = {} } = JSON.parse(config ?? '{}');

  const token: any = useAccessToken();
  const permission: string[] = isImpersonating ? impersonatePermissions : token?.permission ?? [];
  const isAdmin: boolean = token?.isAdmin ?? false;
  const allPermissions = [...permission] as const;

  /**
   * Handles the admin centre permission for a given location.
   * - If we are looking for a permission within the admin centre, first check that we have the admin centre in the menu and if not redirect to 401.
   * - If we have admin centre in the menu, then we can assume permission for everything within there at the moment.
   *
   * ### Temporary work-around to allow access to flows within the admin centre while restricting access to them via the url if admin centre is not present in the menu.
   *
   * @param {typeof allPermissions[number]} location - The location to check for admin centre permission.
   * @return {boolean} Returns true if the location has admin centre permission, otherwise returns undefined.
   */
  const handleAdminCentrePermission = (location: typeof allPermissions[number]) => {
    if (location.includes('settings.')) {
      const flatMenuItems = flattenMenuItems(menuItems);

      const matchingMenuItem = flatMenuItems.find((item: any) => {
        return findItemObject(item, '/uimodule/AdminCentre/');
      });

      if (!matchingMenuItem) {
        return router.push('/401');
      }

      return true;
    }
  };

  const hasWarehousePermission = (location: typeof allPermissions[number]) => {
    if (location.includes('warehouse.')) {
      const flatMenuItems = flattenMenuItems(menuItems);

      const matchingMenuItem = flatMenuItems.find((item: any) => {
        return findItemObject(item, '/uimodule/Warehouse/');
      });

      if (!matchingMenuItem) {
        return router.push('/401');
      }

      return true;
    }
  };

  const hasReportsPermission = (location: typeof allPermissions[number]) => {
    if (location.includes('reports.')) {
      const flatMenuItems = flattenMenuItems(menuItems);

      const matchingMenuItem = flatMenuItems.find((item: any) => {
        return findItemObject(item, '/uimodule/Reports/');
      });

      if (!matchingMenuItem) {
        return router.push('/401');
      }

      return true;
    }
  };

  /**
   * Handle the customer assigned profiles permission as this flow is nested within the customer flow so should be accessible if there is a customer menu item.
   *
   * @param location
   * @returns {boolean | undefined}
   */
  const handleCustomerAssignedProfilesPermission = (_location: typeof allPermissions[number]) => {
    const path = router.asPath;

    if (path.match(/\/uiflow\/customerassignedprofiles\/.*/gim)) {
      const flatMenuItems = flattenMenuItems(menuItems);

      const matchingMenuItem = flatMenuItems.find((item: any) => {
        return findItemObject(item, '/uiflow/Customer/');
      });

      if (!matchingMenuItem) return router.push('/401');

      return true;
    }
  };

  /**
   * Checks if the user has a specific permission for a given location.
   * E.g hasPermission('order.main order data panel', 'CanRead')
   *
   * @param {string} location - The location to check permission for.
   * @param {PermissionType} type - The type of permission to check.
   * @return {boolean} - Returns true if the user has the specified permission, false otherwise.
   */
  const hasPermission = (location: typeof allPermissions[number], type: PermissionType): boolean => {
    if (handleAdminCentrePermission(location)) return true;
    if (hasWarehousePermission(location)) return true;
    if (handleCustomerAssignedProfilesPermission(location)) return true;
    if (hasReportsPermission(location)) return true;

    if (prod === Products.Quantum && isAdmin) return true;

    const filteredPermissions = allPermissions.filter((permission: string) => {
      return permission.includes(location);
    });

    const permissionExistsForType = filteredPermissions.some((permission: string) => {
      return permission.includes(type);
    });

    return permissionExistsForType;
  };

  /**
   * Find a menu item object which matches the current URL path
   * @param item any
   * @param path string
   * @returns boolean
   */
  const findItemObject = (item: any, path: string): boolean => {
    if (!Object.hasOwn(item, 'path')) {
      return false;
    }

    return path.toLowerCase().includes(item.path.toLowerCase() + '/');
  };

  /**
   * Flatten the menu items array
   * @param items any[]
   * @returns any[]
   */
  const flattenMenuItems = (items: any[]): any[] => {
    const flattenedItems = [];

    for (const item of items) {
      if (Object.hasOwn(item, 'path')) {
        flattenedItems.push(item);
      }

      if (Object.hasOwn(item, 'children')) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const childItems = flattenMenuItems(item.children);
        flattenedItems.push(...childItems);
      }
    }

    return flattenedItems;
  };

  /**
   * Determines if a given URL can be routed to a page.
   *
   * @param {string} url - The URL to check.
   */
  const canRouteToPage = (url: string) => {
    const flatMenuItems = flattenMenuItems(menuItems);

    const matchingMenuItem = flatMenuItems.find((item: any) => {
      return findItemObject(item, url);
    });

    if (!matchingMenuItem) return router.push('/401');
  };

  return { hasPermission, findItemObject, flattenMenuItems, canRouteToPage, menuItems, handleAdminCentrePermission };
};

export default usePermission;
